import { FC } from 'react';
import {
  Control,
  FieldError,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import type { StepContentProps } from '../../../types/template.types';
import type { UpdateTemplateFormSchema } from '../../../validations/edit-template.validation';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { stepContentByStep } from '../../../constants/step-mapper';
import { PopupType } from '../../../enums/popup-type.const';
import { TemplateFormStore } from '../../../store/template-form.store';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { MapFieldsModal } from '../map-fields-modal/map-fields-modal';

export type EditTemplateStepsViewProps = {
  clearErrors: UseFormClearErrors<UpdateTemplateFormSchema>;
  control: Control<UpdateTemplateFormSchema>;
  getFieldError: () => FieldError | undefined;
  getValues: UseFormGetValues<UpdateTemplateFormSchema>;
  handleClose: () => void;
  handleSaveClick: () => void;
  isDirty: boolean;
  isNextOrSubmitDisabled: boolean;
  isValid: boolean;
  setError: UseFormSetError<UpdateTemplateFormSchema>;
  setValue: UseFormSetValue<UpdateTemplateFormSchema>;
  templateId: string;
};

export const EditTemplateStepsView: FC<EditTemplateStepsViewProps> = ({
  clearErrors,
  control,
  getFieldError,
  getValues,
  handleClose,
  handleSaveClick,
  isDirty,
  isNextOrSubmitDisabled,
  isValid,
  setError,
  setValue,
  templateId,
}) => {
  const currentStep = TemplateFormStore(({ currentStep }) => currentStep);
  const setStep = TemplateFormStore(({ setStep }) => setStep);
  const popupOpen = TemplatePopupStore(({ open }) => open);
  const popupType = TemplatePopupStore(({ type }) => type);
  const handlePopupDismiss = TemplatePopupStore(({ handleDismiss }) => handleDismiss);

  return (
    <form
      className="flex h-full flex-col overflow-hidden bg-gray-25"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="flex h-full flex-col gap-4 overflow-hidden">
        {stepContentByStep[currentStep]({
          clearErrors: clearErrors,
          control: control as StepContentProps['control'],
          excludeTemplateId: templateId,
          getFieldError: getFieldError,
          getValues: getValues as StepContentProps['getValues'],
          setError: setError,
          setValue: setValue as StepContentProps['setValue'],
        })}
      </div>

      {popupOpen && popupType === PopupType.MAP_FIELDS && (
        <MapFieldsModal
          canSave={isValid}
          control={control}
          hasUnsavedChanges={isDirty}
          initialFieldMapping={getValues().stepThree?.fieldMapping ?? {}}
          initialFieldMappingTypes={getValues().stepThree?.fieldMappingTypes ?? {}}
          initialTabs={getValues().stepThree?.tabs ?? []}
          isOpen
          mode="update"
          onClose={handlePopupDismiss}
          onUpdate={handleSaveClick}
          setValue={setValue}
        />
      )}

      <div className="flex items-center justify-between bg-gray-100 px-6 py-4">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleClose}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={currentStep === 1}
            onClick={() => setStep(currentStep - 1)}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="bg-brand-700 px-6 py-2 text-sm font-medium text-white disabled:bg-gray-100 disabled:text-gray-300"
            disabled={isNextOrSubmitDisabled}
            onClick={currentStep === 3 ? handleSaveClick : () => setStep(currentStep + 1)}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id={currentStep === 3 ? 'UPDATE' : 'NEXT'} />
          </Button>
        </div>
      </div>
    </form>
  );
};
